import React, { useCallback, useEffect, useState } from 'react';
import { Landmark, RefreshCw } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { BalanceSheetCache } from '../../types/finances';
import { refreshBalanceSheet } from '../../utils/financesApi';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

interface BalanceSheetCardProps {
  /** Show even when there's no cached value yet (renders empty state). */
  variant?: 'full' | 'compact';
}

const BalanceSheetCard: React.FC<BalanceSheetCardProps> = ({ variant = 'full' }) => {
  const [cache, setCache] = useState<BalanceSheetCache | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ref = doc(db, 'financeSettings', 'balanceSheetCache');
    const unsub = onSnapshot(
      ref,
      snap => {
        if (!snap.exists()) {
          setCache(null);
        } else {
          const data: any = snap.data();
          setCache({
            totalAssets: data.totalAssets ?? 0,
            totalLiabilities: data.totalLiabilities ?? 0,
            totalEquity: data.totalEquity ?? 0,
            asOfDate: data.asOfDate ?? null,
            lastSyncedAt: data.lastSyncedAt?.toDate?.() ?? null,
          });
        }
        setLoaded(true);
      },
      err => {
        console.warn('[BalanceSheetCard] snapshot error', err);
        setLoaded(true);
      },
    );
    return unsub;
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      await refreshBalanceSheet();
    } catch (err: any) {
      setError(err?.message || 'Unable to refresh');
    } finally {
      setRefreshing(false);
    }
  }, []);

  const hasData = cache && (cache.totalAssets || cache.totalLiabilities || cache.totalEquity);

  return (
    <div className="fin-balance-card">
      <div className="fin-balance-header">
        <h3 className="fin-balance-title">
          <Landmark size={16} aria-hidden="true" /> Balance Sheet
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {cache?.asOfDate && (
            <span className="fin-balance-meta">As of {cache.asOfDate}</span>
          )}
          <button
            type="button"
            className="fin-secondary-btn"
            onClick={refresh}
            disabled={refreshing}
            aria-label="Refresh balance sheet from QuickBooks"
          >
            <RefreshCw size={13} style={{ opacity: refreshing ? 0.4 : 1 }} />
            {refreshing ? 'Refreshing…' : 'Refresh from QuickBooks'}
          </button>
        </div>
      </div>

      {!loaded ? (
        <div className="fin-balance-empty">Loading…</div>
      ) : !hasData ? (
        <div className="fin-balance-empty">
          No balance sheet snapshot yet. Click <strong>Refresh from QuickBooks</strong> to pull the latest figures.
        </div>
      ) : variant === 'compact' ? (
        <div className="fin-balance-grid">
          <div className="fin-balance-cell">
            <span className="fin-balance-cell-label">Net Equity</span>
            <span className={`fin-balance-cell-value ${(cache.totalEquity ?? 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(cache.totalEquity ?? 0)}
            </span>
          </div>
        </div>
      ) : (
        <div className="fin-balance-grid">
          <div className="fin-balance-cell">
            <span className="fin-balance-cell-label">Total Assets</span>
            <span className="fin-balance-cell-value">{formatCurrency(cache.totalAssets ?? 0)}</span>
          </div>
          <div className="fin-balance-cell">
            <span className="fin-balance-cell-label">Total Liabilities</span>
            <span className="fin-balance-cell-value">{formatCurrency(cache.totalLiabilities ?? 0)}</span>
          </div>
          <div className="fin-balance-cell">
            <span className="fin-balance-cell-label">Net Equity</span>
            <span className={`fin-balance-cell-value ${(cache.totalEquity ?? 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(cache.totalEquity ?? 0)}
            </span>
          </div>
        </div>
      )}

      {error && <div className="fin-balance-error">{error}</div>}
    </div>
  );
};

export default BalanceSheetCard;
